import React, { useEffect } from 'react'
import ProgramEditingSubSection from './Components/ProgramEditingSubSection'
import GRHUtils from 'features/GRH/Shared/GRHUtils'
import BudgetUsesSelector from './Components/BudgetUsesSelector'
import ProgramOption from './Components/ProgramOption'
import {
  grhType,
  grhActiveOptionsType,
  grhTransactionUsesType,
  availableGrhMerchant,
} from '../Shared/types'
import { GRH_INITIAL_STATE } from './ProgramEditingSlice'
import { useGateValue } from '@statsig/react-bindings'

interface GrhProgramEditingProps {
  grh: grhType
  availableMerchants: availableGrhMerchant
  getGrhRecommendations: ({ segmentId }: { segmentId: string }) => void
  changeGrhActiveMerchantIds: (
    type: grhActiveOptionsType,
    value: string[]
  ) => void
  changeGrhTransactionUses: (
    type: grhTransactionUsesType,
    value: number
  ) => void
  changeGrhActiveOptions: (type: grhActiveOptionsType, value: boolean) => void
  getGovernmentGrhPrograms: () => void
}

const GrhProgramEditing = ({
  grh = GRH_INITIAL_STATE,
  availableMerchants,
  getGrhRecommendations,
  changeGrhActiveMerchantIds,
  changeGrhTransactionUses,
  changeGrhActiveOptions,
  getGovernmentGrhPrograms,
}: GrhProgramEditingProps) => {
  useEffect(() => {
    getGovernmentGrhPrograms()
    getGrhRecommendations({ segmentId: '' })
  }, [])

  const rideShareMerchantIds =
    availableMerchants?.rideshare.map((m) => m.id) || []
  const localTaxiMerchantIds =
    availableMerchants?.localTaxi.map((m) => m.id) || []

  const isGovernmentGrhEnabled = useGateValue('government_grh')

  useEffect(() => {
    if (availableMerchants) {
      changeGrhActiveMerchantIds('rideshare', rideShareMerchantIds)
      changeGrhActiveMerchantIds('localTaxi', localTaxiMerchantIds)
    }
  }, [availableMerchants])

  const changeTransactionLimit = (newLimit: number) => {
    changeGrhTransactionUses('transaction', newLimit * 100)
  }

  const changeUses = (newUses: number) => {
    changeGrhTransactionUses('uses', newUses)
  }

  const toggleRideshare = (type: grhActiveOptionsType) => {
    const newActive = !grh.activeOptions[type]
    changeGrhActiveOptions(type, newActive)
  }

  const toggleLocalTaxi = (type: grhActiveOptionsType) => {
    const newActive = !grh.activeOptions[type]
    changeGrhActiveOptions(type, newActive)
  }

  const toggleGovernmentGrh = (type: grhActiveOptionsType) => {
    const newActive = !grh.activeOptions[type]
    changeGrhActiveOptions(type, newActive)
  }

  return (
    <>
      <ProgramEditingSubSection>
        <BudgetUsesSelector
          budget={grh.transaction}
          onBudgetChange={changeTransactionLimit}
          recBudget={GRHUtils.recTransacLimit}
          uses={grh.uses}
          recUses={GRHUtils.recUses}
          onUsesChange={changeUses}
          budgetError={grh.transaction <= 0}
          usesError={grh.uses <= 0}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        {isGovernmentGrhEnabled && (
          <ProgramOption
            type='governmentGrh'
            active={grh.activeOptions.governmentGrh}
            handleToggle={toggleGovernmentGrh}
            governmentGrhPrograms={grh.governmentGrhPrograms}
          />
        )}
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramOption
          type='rideshare'
          active={grh.activeOptions.rideshare}
          handleToggle={toggleRideshare}
          availableMerchantIds={rideShareMerchantIds}
          activeMerchantIds={grh.activeMerchantIds.rideshare}
          readOnly={true}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection isLastSection>
        <ProgramOption
          type='localTaxi'
          active={grh.activeOptions.localTaxi}
          handleToggle={toggleLocalTaxi}
          availableMerchantIds={localTaxiMerchantIds}
          activeMerchantIds={grh.activeMerchantIds.localTaxi}
          readOnly={true}
        />
      </ProgramEditingSubSection>
    </>
  )
}

export default React.memo(GrhProgramEditing)
